import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Trophy, CheckCircle2, Circle, Clock, RefreshCw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { tournamentsAPI } from '@/lib/api'
import { cn } from '@/lib/utils'
import { ParticipantName } from './BracketTreeView'

/* Swiss-format bracket-view.
   Bovenaan: klassement (wins/losses/BYE + Buchholz).
   Daaronder: matches per ronde. Admin krijgt de "Volgende ronde"-knop
   zodra alle matches van de huidige ronde afgesloten zijn EN er nog
   geplande rondes over zijn (op basis van features.swiss_rounds). */
export default function SwissBracket({ tournamentId, matches, participantHrefs = {}, onMatchClick, isAdmin }) {
  const [standings, setStandings] = useState([])
  const [swissRounds, setSwissRounds] = useState(0)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const loadStandings = async () => {
    setLoading(true)
    try {
      const r = await tournamentsAPI.getSwissStandings(tournamentId)
      setStandings(r.data.standings || [])
      setSwissRounds(Number(r.data.swiss_rounds || 0))
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally { setLoading(false) }
  }

  useEffect(() => { if (tournamentId) loadStandings() }, [tournamentId, matches?.length])

  // Groepeer matches per ronde. Match_number sorteert binnen ronde.
  const byRound = useMemo(() => {
    const g = {}
    ;(matches || []).forEach(m => { (g[m.round] ||= []).push(m) })
    Object.values(g).forEach(a => a.sort((x, y) => x.match_number - y.match_number))
    return g
  }, [matches])
  const rounds = Object.keys(byRound).map(Number).sort((a, b) => a - b)
  const currentRound = rounds.length ? Math.max(...rounds) : 0

  // Alle matches van de laatste ronde af? Dan mag admin volgende genereren.
  const lastRoundMatches = byRound[currentRound] || []
  const lastRoundDone = lastRoundMatches.length > 0 && lastRoundMatches.every(m => m.status === 'completed')
  const hasMoreRounds = currentRound < swissRounds
  const canGenerate = isAdmin && lastRoundDone && hasMoreRounds

  const doGenerate = async () => {
    setBusy(true); setError(null)
    try {
      await tournamentsAPI.generateNextSwissRound(tournamentId)
      await loadStandings()
      // matches worden geladen door parent-component via socket 'tournament:updated'
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  if (!matches || matches.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Trophy className="h-16 w-16 mx-auto mb-4 opacity-50" />
        <p>Nog geen matches. Start het toernooi om ronde 1 te genereren.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Klassement */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Trophy className="h-4 w-4 text-acid" />
              Klassement
              {swissRounds > 0 && (
                <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                  ronde {currentRound}/{swissRounds}
                </span>
              )}
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={loadStandings} disabled={loading}>
              <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border/40">
                  <th className="text-left py-2 pr-2 w-10">#</th>
                  <th className="text-left py-2 pr-2">Deelnemer</th>
                  <th className="text-right py-2 px-2 w-14">W</th>
                  <th className="text-right py-2 px-2 w-14">V</th>
                  <th className="text-right py-2 px-2 w-14">BYE</th>
                  <th className="text-right py-2 px-2 w-16">Pts</th>
                  <th className="text-right py-2 pl-2 w-16 cursor-help"
                      title="Buchholz — som van de scores van je opponenten. Tiebreak bij gelijke punten: hoger = tegen sterkere spelers gespeeld."><Link to="/wiki/begrippen" className="hover:text-acid">Buch.</Link></th>
                </tr>
              </thead>
              <tbody>
                {standings.map(s => (
                  <tr key={s.participant_id}
                      className={cn(
                        'border-b border-border/20 last:border-0',
                        s.no_show && 'opacity-40 line-through'
                      )}>
                    <td className="py-1.5 pr-2 font-mono text-xs">{s.rank}</td>
                    <td className="py-1.5 pr-2 font-semibold truncate max-w-[240px]">
                      <ParticipantName name={s.team_name} href={participantHrefs[s.participant_id]} />
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{s.wins}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-muted-foreground">{s.losses}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-muted-foreground">{s.byes}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums font-bold text-acid">{s.score}</td>
                    <td className="py-1.5 pl-2 text-right tabular-nums text-muted-foreground">{s.buchholz}</td>
                  </tr>
                ))}
                {!standings.length && (
                  <tr><td colSpan="7" className="text-center py-4 text-muted-foreground">Nog geen resultaten.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Actiebalk voor volgende ronde */}
      {isAdmin && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm text-muted-foreground">
            {!hasMoreRounds
              ? <span className="text-live">Alle {swissRounds} rondes zijn gepaird. Speel af — dan sluit het toernooi automatisch.</span>
              : lastRoundDone
                ? <span className="text-acid">Ronde {currentRound} is volledig gespeeld.</span>
                : <span>Wacht tot ronde {currentRound} volledig is voor de volgende ronde.</span>}
          </div>
          <Button size="sm" disabled={!canGenerate || busy}
                  variant={canGenerate ? 'default' : 'outline'}
                  onClick={doGenerate}>
            {busy ? 'Bezig…' : `Genereer ronde ${currentRound + 1}`}
          </Button>
        </div>
      )}

      {error && <div className="text-sm text-signal">{error}</div>}

      {/* Rondes */}
      {rounds.slice().reverse().map(r => (
        <RoundBlock key={r}
                    round={r}
                    matches={byRound[r]}
                    participantHrefs={participantHrefs}
                    onMatchClick={onMatchClick}
                    isAdmin={isAdmin} />
      ))}
    </div>
  )
}

function RoundBlock({ round, matches, participantHrefs = {}, onMatchClick, isAdmin }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">
          Ronde {round}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2 md:grid-cols-2">
          {matches.map(m => (
            <SwissMatchCard key={m.id} match={m}
                            participantHrefs={participantHrefs}
                            onClick={() => isAdmin && m.participant1_id && m.participant2_id
                                           && onMatchClick && onMatchClick(m)}
                            clickable={isAdmin && m.participant1_id && m.participant2_id} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function SwissMatchCard({ match, participantHrefs = {}, onClick, clickable }) {
  const isBye = match.score === 'BYE'
  const chip = () => {
    if (isBye) return <Badge variant="outline" className="text-[10px]">BYE</Badge>
    switch (match.status) {
      case 'completed':   return <CheckCircle2 className="h-3 w-3 text-live" />
      case 'in_progress': return <Clock className="h-3 w-3 text-live" />
      default:            return <Circle className="h-3 w-3 text-muted-foreground" />
    }
  }

  const [scoreA, scoreB] = (() => {
    if (isBye || !match.score) return [null, null]
    const m = String(match.score).match(/^(\d+)\s*[-:]\s*(\d+)$/)
    return m ? [m[1], m[2]] : [null, null]
  })()

  return (
    <div
      onClick={clickable ? onClick : undefined}
      className={cn(
        'rounded-md border border-border p-2.5 space-y-1',
        clickable && 'cursor-pointer hover:border-acid transition-colors'
      )}>
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        <span>Match {match.match_number}{Number(match.best_of || 1) > 1 && <span className="ml-1 text-acid">BO{match.best_of}</span>}</span>
        <span className="flex items-center gap-1">{chip()}</span>
      </div>
      <ParticipantLine name={match.participant1_name}
                       href={participantHrefs[match.participant1_id]}
                       isWinner={!!match.winner_id && match.winner_id === match.participant1_id}
                       isNull={!match.participant1_id}
                       score={scoreA} />
      <ParticipantLine name={match.participant2_name}
                       href={participantHrefs[match.participant2_id]}
                       isWinner={!!match.winner_id && match.winner_id === match.participant2_id}
                       isNull={!match.participant2_id}
                       score={scoreB} />
      {isBye && (
        <div className="text-center text-[10px] font-mono uppercase tracking-widest text-muted-foreground pt-1 border-t border-border/40">
          Bye — 1 punt gratis
        </div>
      )}
      {match.game_scores && !isBye && (
        <div className="text-center text-[10px] font-mono tabular-nums text-muted-foreground pt-1 border-t border-border/40 truncate">
          {match.game_scores}
        </div>
      )}
    </div>
  )
}

function ParticipantLine({ name, href, isWinner, isNull, score }) {
  return (
    <div className={cn(
      'flex items-center gap-2 px-2 py-1 rounded text-sm',
      isWinner ? 'bg-live/15 text-live font-semibold'
               : isNull ? 'bg-muted/20 text-muted-foreground italic'
                        : 'bg-card/60'
    )}>
      <ParticipantName name={name || (isNull ? '—' : 'TBD')} href={href}
                       className="truncate flex-1" />
      {score != null && (
        <span className={cn('font-mono tabular-nums font-bold shrink-0 min-w-[1.5rem] text-right',
              isWinner ? 'text-live' : 'text-muted-foreground')}>
          {score}
        </span>
      )}
    </div>
  )
}
